<script setup>
import { computed, defineProps } from 'vue'

const props = defineProps({
  // One hero item (IRRE row) as flattened from PHP
  card: {
    type: Object,
    required: true,
  },
})

// flexform settings from CE
const settings = computed(() => props.card?.settings ?? {})

/**
 * Helper: first media entry by field name
 * (e.g. 'image', 'image_tablet', 'image_mobile', 'video', 'badge')
 */
const mediaByField = (field) => {
  const list = Array.isArray(props.card?.media) ? props.card.media : []
  const key = String(field || '').toLowerCase()
  return list.find(m => (m?.field || '').toLowerCase() === key) || null
}

const imageDesktop = computed(() => mediaByField('image'))
const imageTablet = computed(() => mediaByField('image_tablet'))
const imageMobile = computed(() => mediaByField('image_mobile'))
const videoMedia = computed(() => mediaByField('video'))
const badgeImage = computed(() => mediaByField('badge'))

const hasTabletOrMobile = computed(() => !!imageTablet.value || !!imageMobile.value)
const hasAnyTitleOrLink = computed(
  () =>
    !!props.card?.title ||
    !!props.card?.subtitle ||
    (!!props.card?.link && !!props.card?.linktext),
)

/**
 * Outer wrapper classes:
 * .section-row.module-hero + flags from settings
 */
const wrapperClasses = computed(() => ([
  'section-row',
  'module-hero',
  {
    '-fully-linked': !!settings.value.linkedHero,
    '-hide-headlines': !!settings.value.hideHeadline,
    '-hide-ab-test': !!settings.value.hideForABTest,
    '-hide-mobile': !!settings.value.hideMobile,
    '-hide-tablet': !!settings.value.hideTablet,
    '-hide-desktop': !!settings.value.hideDesktop,
  },
]))

/**
 * Item classes (no swiper-slide, just hero__item etc.)
 */
const itemClasses = computed(() => {
  const cls = ['hero__item']
  const imagesize = props.card?.imagesize

  if (imagesize && !hasTabletOrMobile.value) {
    cls.push(imagesize)
  }

  if (hasTabletOrMobile.value) {
    cls.push('-mobile-images')
  }

  if (props.card?.showImageOverlay) {
    cls.push('-blurred')
  }

  if (videoMedia.value && hasAnyTitleOrLink.value) {
    cls.push('-mobile-text-bottom', '-mobile-text-bottom-placeholder')
  }

  return cls
})

/**
 * Inline style vars: overlay width/height (mobile)
 */
const itemStyle = computed(() => {
  const style = {}
  if (props.card?.imageOverlayWidth) {
    style['--image-overlay-width'] = props.card.imageOverlayWidth
  }
  if (props.card?.imageOverlayHeightMobile) {
    style['--image-overlay-height'] = props.card.imageOverlayHeightMobile
  }
  return style
})

/**
 * Blur overlay classes
 */
const overlayBlurClasses = computed(() => {
  const cls = ['overlay__blur-bg']
  if (props.card?.imageOverlayHorizontalPlacement) {
    cls.push(props.card.imageOverlayHorizontalPlacement)
  }
  if (props.card?.imageOverlayVerticalPlacement) {
    cls.push(props.card.imageOverlayVerticalPlacement)
  }
  return cls
})

/**
 * Content positioning classes
 */
const contentClasses = computed(() => {
  const cls = ['item__content']
  if (props.card?.showImageOverlay) {
    if (props.card?.imageOverlayHorizontalPlacement) {
      cls.push(props.card.imageOverlayHorizontalPlacement)
    }
    if (props.card?.contentVerticalPlacement) {
      cls.push(props.card.contentVerticalPlacement)
    }
    if (props.card?.textAlignment) {
      cls.push(`-text-${props.card.textAlignment}`)
    }
  }
  return cls
})

/**
 * Pagination title/subtitle fallback logic
 * (used as data attributes, but no Swiper)
 */
const paginationTitle = computed(
  () => props.card?.title_preview || props.card?.title || '',
)
const paginationSubtitle = computed(
  () => props.card?.subtitle_preview || props.card?.subtitle || '',
)
</script>

<template>
  <div
    :class="wrapperClasses"
    :id="settings.anchor || null"
  >
    <component
      :is="settings.linkedHero ? 'a' : 'div'"
      class="item-wrapper"
      :class="itemClasses"
      :style="itemStyle"
      :href="settings.linkedHero && card.link ? card.link : undefined"
    >
      <div
        v-if="card.showImageOverlay"
        :class="overlayBlurClasses"
      ></div>
      <div class="item__image">
        <div
          v-if="videoMedia"
          class="item__video"
        >
          <div class="video-wrapper">
            <video playsinline muted autoplay loop>
              <source :src="videoMedia.url" type="video/mp4" />
            </video>
          </div>
        </div>
        <template v-if="imageDesktop || imageTablet || imageMobile">
          <div
            v-if="imageDesktop"
            class="image__image image__image--desktop"
          >
            <img
              :src="imageDesktop.url"
              :alt="imageDesktop.alt || card.title || ''"
              :title="imageDesktop.title || ''"
              loading="lazy"
            />
          </div>
          <div
            v-if="imageTablet"
            class="image__image image__image--tablet"
          >
            <img
              :src="imageTablet.url"
              :alt="imageTablet.alt || card.title || ''"
              :title="imageTablet.title || ''"
              loading="lazy"
            />
          </div>
          <div
            v-if="imageMobile"
            class="image__image image__image--mobile"
          >
            <img
              :src="imageMobile.url"
              :alt="imageMobile.alt || card.title || ''"
              :title="imageMobile.title || ''"
              loading="lazy"
            />
          </div>
        </template>
      </div>
      <div
        v-if="badgeImage"
        class="item__badge"
      >
        <img
          :src="badgeImage.url"
          :alt="badgeImage.alt || ''"
          :title="badgeImage.title || ''"
          loading="lazy"
        />
      </div>
      <div
        v-if="card.title || card.subtitle"
        :class="contentClasses"
      >
        <div class="content-wrapper -max-width-limited">
          <div class="content__content">
            <div
              v-if="card.title"
              class="content__title"
              :class="card.color"
              :data-pagination-title="paginationTitle"
            >
              <component
                :is="card.titleType || 'h2'"
                class="heading alpha"
              >
                {{ card.title }}
              </component>
            </div>
            <div
              v-if="card.subtitle"
              class="content__subtitle"
              :class="card.color"
              :data-pagination-subtitle="paginationSubtitle"
            >
              <component
                :is="card.subtitleType || 'p'"
                class="heading beta"
              >
                {{ card.subtitle }}
              </component>
            </div>
            <div
              v-if="!settings.linkedHero && card.link && card.linktext"
              class="content__link"
            >
              <a
                :href="card.link"
                class="-btn-alpha"
              >
                <span>{{ card.linktext }}</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </component>
  </div>
</template>
